import { useState, type FormEvent } from "react";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { NewPasswordFields, newPasswordReady } from "../../components/admin/PasswordFields";

interface AccountScreenProps {
  name: string;
  email: string;
  roleLabel: string;
  mustChange: boolean;
  busy: boolean;
  error: string | null;
  message: string | null;
  onChangePassword: (currentPassword: string, newPassword: string) => void;
}

/** The admin's own account: who they are, and changing their password. */
export function AccountScreen({
  name,
  email,
  roleLabel,
  mustChange,
  busy,
  error,
  message,
  onChangePassword,
}: AccountScreenProps) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const ready = Boolean(current) && newPasswordReady(next, confirm, email);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onChangePassword(current, next);
  };

  return (
    <div className="grid max-w-[880px] grid-cols-1 gap-4 lg:grid-cols-[1fr_1.3fr]">
      <div className="h-fit rounded-[14px] border border-border bg-surface p-5 text-[14px]">
        <div className="text-[11.5px] font-bold uppercase tracking-[0.07em] text-text-muted">
          Signed in as
        </div>
        <div className="mt-1 text-[17px] font-bold">{name}</div>
        <div className="text-text-muted">{email}</div>
        <div className="mt-2 text-text-muted">{roleLabel}</div>
      </div>

      <form onSubmit={submit} className="rounded-[14px] border border-border bg-surface p-5">
        <h2 className="mb-1 text-[17px]">Change password</h2>
        <p className="mb-4 mt-0 text-[13.5px] text-text-muted">
          Changing it signs you out everywhere else, so a lost phone or laptop stays signed out.
        </p>
        {mustChange ? (
          <Banner tone="warn">
            Your password was set by a super admin. Choose your own to start using the admin
            workspace.
          </Banner>
        ) : null}
        <Field
          label={mustChange ? "Temporary password" : "Current password"}
          htmlFor="current-password"
        >
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            className={inputShell}
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>
        <NewPasswordFields
          email={email}
          value={next}
          confirm={confirm}
          onChange={setNext}
          onConfirm={setConfirm}
        />
        {error ? (
          <p role="alert" className="mb-3 text-[13.5px] text-negative">
            {error}
          </p>
        ) : null}
        {message ? <Banner tone="good">{message}</Banner> : null}
        <Button type="submit" disabled={busy || !ready}>
          {busy ? "Saving..." : "Change password"}
        </Button>
      </form>
    </div>
  );
}
